/**
 * Google Identity Services plumbing: script load, hidden-button warm-up,
 * and ID-token acquisition. Product auth flow lives in googleAuth.ts.
 */

const GIS_SCRIPT_SRC = "https://accounts.google.com/gsi/client";
/** Safety net if Google never notifies us after the user closes the chooser. */
const GOOGLE_CHOOSER_TIMEOUT_MS = 60_000;
/** After the tab regains focus, wait briefly for a late credential before cancelling. */
const FOCUS_CANCEL_GRACE_MS = 3_000;

type GoogleCredentialResponse = {
  credential: string;
};

type GoogleAccountsId = {
  initialize: (config: {
    client_id: string;
    callback: (response: GoogleCredentialResponse) => void;
    auto_select?: boolean;
    cancel_on_tap_outside?: boolean;
    use_fedcm_for_prompt?: boolean;
    itp_support?: boolean;
  }) => void;
  prompt: (momentListener?: (notification: unknown) => void) => void;
  renderButton: (parent: HTMLElement, options: Record<string, string | number | boolean>) => void;
  cancel: () => void;
};

declare global {
  interface Window {
    google?: {
      accounts: {
        id: GoogleAccountsId;
      };
    };
  }
}

export class GoogleNotConfiguredError extends Error {
  constructor() {
    super("google_not_configured");
    this.name = "GoogleNotConfiguredError";
  }
}

let scriptPromise: Promise<void> | null = null;
let warmPromise: Promise<HTMLElement> | null = null;
let warmButton: HTMLElement | null = null;
let warmContainer: HTMLDivElement | null = null;
let gisInitialized = false;
/** Mutable so we can initialize GIS once and swap handlers per click. */
let credentialHandler: ((response: GoogleCredentialResponse) => void) | null = null;

export function getGoogleClientId(): string {
  const clientId = import.meta.env.VITE_GOOGLE_WEB_CLIENT_ID?.trim();
  if (!clientId) {
    throw new GoogleNotConfiguredError();
  }
  return clientId;
}

export function isGoogleGisConfigured(): boolean {
  try {
    getGoogleClientId();
    return true;
  } catch {
    return false;
  }
}

export function resetGoogleGis(): void {
  credentialHandler = null;
  warmPromise = null;
  warmButton = null;
  gisInitialized = false;

  if (warmContainer?.parentNode) {
    warmContainer.parentNode.removeChild(warmContainer);
  }
  warmContainer = null;

  try {
    window.google?.accounts.id.cancel();
  } catch {
    // ignore
  }
}

function loadGisScript(): Promise<void> {
  if (window.google?.accounts?.id) {
    return Promise.resolve();
  }
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${GIS_SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("google_gis_script_failed")), {
        once: true,
      });
      if (window.google?.accounts?.id) resolve();
      return;
    }

    const script = document.createElement("script");
    script.src = GIS_SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("google_gis_script_failed"));
    document.head.appendChild(script);
  }).catch((error) => {
    scriptPromise = null;
    throw error;
  });

  return scriptPromise;
}

function ensureInitialized(): GoogleAccountsId {
  const googleId = window.google?.accounts?.id;
  if (!googleId) {
    throw new Error("google_gis_unavailable");
  }

  if (!gisInitialized) {
    googleId.initialize({
      client_id: getGoogleClientId(),
      callback: (response) => {
        credentialHandler?.(response);
      },
      auto_select: false,
      cancel_on_tap_outside: true,
      // FedCM + strict COOP often delays/blocks the credential postMessage after
      // account selection; classic GIS popup returns the token promptly.
      use_fedcm_for_prompt: false,
      itp_support: true,
    });
    gisInitialized = true;
  }

  return googleId;
}

function waitForWarmButton(container: HTMLElement): Promise<HTMLElement> {
  const existing = container.querySelector<HTMLElement>("div[role=button]");
  if (existing) return Promise.resolve(existing);

  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      observer.disconnect();
      reject(new Error("google_button_render_timeout"));
    }, 10_000);

    const observer = new MutationObserver(() => {
      const btn = container.querySelector<HTMLElement>("div[role=button]");
      if (btn) {
        window.clearTimeout(timeout);
        observer.disconnect();
        resolve(btn);
      }
    });
    observer.observe(container, { childList: true, subtree: true });
  });
}

/**
 * Pre-render Google's button iframe off-screen so a click can open the
 * account chooser immediately (no One Tap wait, no cold iframe load).
 */
function ensureWarmButton(): Promise<HTMLElement> {
  if (warmButton?.isConnected) {
    return Promise.resolve(warmButton);
  }

  if (warmPromise) {
    return warmPromise.then((button) => {
      if (button.isConnected) return button;
      warmPromise = null;
      warmButton = null;
      return ensureWarmButton();
    });
  }

  warmPromise = (async () => {
    await loadGisScript();
    const googleId = ensureInitialized();

    if (warmContainer?.parentNode) {
      warmContainer.parentNode.removeChild(warmContainer);
    }

    warmContainer = document.createElement("div");
    warmContainer.setAttribute("aria-hidden", "true");
    warmContainer.style.position = "fixed";
    warmContainer.style.left = "-9999px";
    warmContainer.style.top = "0";
    warmContainer.style.width = "1px";
    warmContainer.style.height = "1px";
    warmContainer.style.overflow = "hidden";
    document.body.appendChild(warmContainer);

    googleId.renderButton(warmContainer, {
      type: "standard",
      theme: "outline",
      size: "large",
      text: "continue_with",
      width: 320,
    });

    const button = await waitForWarmButton(warmContainer);
    warmButton = button;
    return button;
  })().catch((error) => {
    warmPromise = null;
    warmButton = null;
    throw error;
  });

  return warmPromise;
}

/**
 * Start loading GIS + warming the hidden Google button as soon as auth UI mounts.
 * Safe to call multiple times.
 */
export function preloadGoogleGis(): void {
  if (!isGoogleGisConfigured()) return;
  void ensureWarmButton().catch(() => {
    // Warm-up is best-effort; click path will retry.
  });
}

function stringClaim(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function profileFromIdToken(idToken: string): { email: string; fullName: string | null } | null {
  try {
    const payloadPart = idToken.split(".")[1];
    if (!payloadPart) return null;
    const json = atob(payloadPart.replace(/-/g, "+").replace(/_/g, "/"));
    const payload = JSON.parse(json) as {
      email?: unknown;
      name?: unknown;
      given_name?: unknown;
      family_name?: unknown;
    };
    const email = stringClaim(payload.email);
    if (!email) return null;
    const fullName =
      stringClaim(payload.name) ||
      [stringClaim(payload.given_name), stringClaim(payload.family_name)]
        .filter(Boolean)
        .join(" ") ||
      null;
    return { email, fullName };
  } catch {
    return null;
  }
}

export type GoogleIdTokenResult =
  | "cancelled"
  | { idToken: string; email: string; fullName: string | null };

async function requestGoogleIdTokenOnce(): Promise<GoogleIdTokenResult> {
  const button = await ensureWarmButton();

  return new Promise((resolve, reject) => {
    let settled = false;
    let focusCancelTimer: number | null = null;
    let safetyTimer: number | null = null;
    let sawBlur = false;

    const cleanup = () => {
      credentialHandler = null;
      if (focusCancelTimer != null) window.clearTimeout(focusCancelTimer);
      if (safetyTimer != null) window.clearTimeout(safetyTimer);
      window.removeEventListener("focus", onWindowFocus);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("blur", onWindowBlur);
      try {
        window.google?.accounts.id.cancel();
      } catch {
        // ignore
      }
    };

    const finish = (value: GoogleIdTokenResult) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(value);
    };

    const fail = (error: unknown) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(error);
    };

    const scheduleCancelAfterReturn = () => {
      if (settled || !sawBlur) return;
      if (focusCancelTimer != null) window.clearTimeout(focusCancelTimer);
      focusCancelTimer = window.setTimeout(() => {
        if (!settled) finish("cancelled");
      }, FOCUS_CANCEL_GRACE_MS);
    };

    function onWindowBlur(): void {
      sawBlur = true;
    }

    function onWindowFocus(): void {
      scheduleCancelAfterReturn();
    }

    function onVisibilityChange(): void {
      if (document.visibilityState === "hidden") {
        sawBlur = true;
        return;
      }
      if (document.visibilityState === "visible") {
        scheduleCancelAfterReturn();
      }
    }

    credentialHandler = (response: GoogleCredentialResponse) => {
      const idToken = response.credential;
      const profile = profileFromIdToken(idToken);
      if (!profile) {
        fail(new Error("missing_google_email"));
        return;
      }
      finish({ idToken, email: profile.email, fullName: profile.fullName });
    };

    window.addEventListener("blur", onWindowBlur);
    window.addEventListener("focus", onWindowFocus);
    document.addEventListener("visibilitychange", onVisibilityChange);
    safetyTimer = window.setTimeout(() => {
      if (!settled) finish("cancelled");
    }, GOOGLE_CHOOSER_TIMEOUT_MS);

    try {
      // Open the chooser on the warmed GIS button (user gesture still active when
      // warm-up finished before this click).
      button.click();
    } catch (error) {
      fail(error);
    }
  });
}

/**
 * Opens Google account chooser and returns an ID token (JWT).
 * Closing / dismissing the chooser resolves as "cancelled" (no hanging promise).
 */
export async function requestGoogleIdToken(): Promise<GoogleIdTokenResult> {
  try {
    return await requestGoogleIdTokenOnce();
  } catch (firstError) {
    resetGoogleGis();
    try {
      return await requestGoogleIdTokenOnce();
    } catch {
      throw firstError;
    }
  }
}
